/**
 * What build this is — G7.5: "the UI footer displays the version".
 *
 * Two surfaces state it: the rail's foot, once a planner is in, and the sign-in screen,
 * before they are. It lives here rather than in `App.tsx` because the sign-in screen
 * renders *instead of* the chrome-and-router tree rather than inside it, so there is no
 * prop path between them — and two copies of `import.meta.env['VITE_APP_VERSION'] ?? …`
 * is two places for the fallback to drift.
 *
 * The fallbacks are what a developer's build shows. A release sets both (`release.yml`
 * passes the tag and the SHA), and a screenshot from a deployed tier can therefore always
 * be traced to a commit, which is what G7.5 is for.
 *
 * `vite.config.ts` now always supplies the version — the release's, or `<package.json>-dev` —
 * so the literal below is reached only where Vite's config is not in play (the unit tests).
 * It said `0.1.0-dev` and a 0.7.0 build showed it in its footer: audit v4 G-18.
 */

/**
 * The version as a bare number, without a leading `v`. The catalogue adds the `v` where it wants
 * one (`app.versionShort` is `v{version} · {commit}`), and `release.yml` passes the tag itself —
 * `v0.37.0` — because the same value is the image tag. Taking it as given printed `vv0.37.0` in
 * the rail's foot from #118 onwards, so it is normalised once, here, where both surfaces read it.
 */
export function bareVersion(version: string): string {
  return version.replace(/^v(?=\d)/, '');
}

export const APP_VERSION: string = bareVersion(import.meta.env['VITE_APP_VERSION'] ?? 'unversioned-dev');
export const COMMIT_SHA: string = import.meta.env['VITE_COMMIT_SHA'] ?? 'local';
